import React, { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SaaSAuthProvider, useSaaSAuth } from "./contexts/SaaSAuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";

// Public marketing / auth pages
import LandingPage from "./pages/LandingPage";
import SigninPage from "./pages/SigninPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import ContactPage from "./pages/ContactPage";

// Protected application pages
import DashboardPage from "./pages/DashboardPage";
import CompaniesPage from "./pages/CompaniesPage";
import CompanyDetailPage from "./pages/CompanyDetailPage";
import ContactsPage from "./pages/ContactsPage";
import ReviewContactsPage from "./pages/ReviewContactsPage";
import ReviewQueuePage from "./pages/ReviewQueuePage";
import DiscoveryJobsPage from "./pages/DiscoveryJobsPage";
import ImportsPage from "./pages/ImportsPage";
import ExportsPage from "./pages/ExportsPage";
import ScoringRulesPage from "./pages/ScoringRulesPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import AuditLogPage from "./pages/AuditLogPage";
import SystemHealthPage from "./pages/SystemHealthPage";
import HelpPage from "./pages/HelpPage";
import BillingPage from "./pages/BillingPage";
import SubscriptionAdminPage from "./pages/SubscriptionAdminPage";
import ProductsAdminPage from "./pages/ProductsAdminPage";

function Spinner() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-foreground border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-muted-foreground font-mono">{t('common.loading')}</p>
      </div>
    </div>
  );
}

function RequireSuperAdmin({ children }) {
  const { user, loading } = useSaaSAuth();
  if (loading) return <Spinner />;
  if (user?.role !== 'super_admin') return <Navigate to="/app" replace />;
  return children;
}

function RequireAuth({ children }) {
  const { user, loading } = useSaaSAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/signin" replace />;
  return children;
}

function RedirectIfAuthed({ children }) {
  const { user, loading } = useSaaSAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  useEffect(() => {
    document.title = "Mayr Technologies";
    const stripEmergentChrome = () => {
      if (document.title.includes("Emergent")) {
        document.title = "Mayr Technologies";
      }
      document.getElementById("emergent-badge")?.remove();
      document.querySelectorAll('a[href*="emergent"]').forEach((el) => el.remove());
      document.querySelectorAll("a").forEach((el) => {
        if ((el.textContent || "").includes("Made with Emergent")) {
          el.remove();
        }
      });
    };
    stripEmergentChrome();
    const timers = [0, 100, 500, 1500, 3000].map((ms) =>
      window.setTimeout(stripEmergentChrome, ms),
    );
    const iv = window.setInterval(stripEmergentChrome, 2000);
    const stopIv = window.setTimeout(() => window.clearInterval(iv), 12000);
    const titleIv = window.setInterval(() => {
      if (document.title.includes("Emergent")) {
        document.title = "Mayr Technologies";
      }
    }, 500);
    const stopTitle = window.setTimeout(() => window.clearInterval(titleIv), 30000);
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      window.clearInterval(iv);
      window.clearTimeout(stopIv);
      window.clearInterval(titleIv);
      window.clearTimeout(stopTitle);
    };
  }, []);

  return (
    <ThemeProvider>
      <SaaSAuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public marketing site */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Auth pages */}
            <Route path="/signin" element={<RedirectIfAuthed><SigninPage /></RedirectIfAuthed>} />
            <Route path="/signup" element={<RedirectIfAuthed><SignupPage /></RedirectIfAuthed>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Legacy /login alias → signin */}
            <Route path="/login" element={<Navigate to="/signin" replace />} />

            {/* Protected customer app */}
            <Route path="/app" element={<RequireAuth><Layout /></RequireAuth>}>
              <Route index element={<DashboardPage />} />
              <Route path="companies" element={<CompaniesPage />} />
              <Route path="companies/:id" element={<CompanyDetailPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="review-contacts" element={<ReviewContactsPage />} />
              <Route path="review" element={<ReviewQueuePage />} />
              <Route path="discovery" element={<DiscoveryJobsPage />} />
              <Route path="imports" element={<ImportsPage />} />
              <Route path="exports" element={<ExportsPage />} />
              <Route path="scoring" element={<ScoringRulesPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="subscriptions" element={<RequireSuperAdmin><SubscriptionAdminPage /></RequireSuperAdmin>} />
              <Route path="products" element={<RequireSuperAdmin><ProductsAdminPage /></RequireSuperAdmin>} />
              <Route path="users" element={<UsersPage />} />
              <Route path="audit-logs" element={<AuditLogPage />} />
              <Route path="system-health" element={<SystemHealthPage />} />
              <Route path="help" element={<HelpPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </SaaSAuthProvider>
    </ThemeProvider>
  );
}

export default App;
